import { createAudio } from '@fg/audio';
import { createDebugOverlay } from '@fg/debug';
import { createLifecycle } from '@fg/lifecycle';
import { platform } from '@fg/platform-sdk';
import { detectQuality } from '@fg/quality';
import { type Rng, createRng } from '@fg/rng';
import { type Storage, createStorage } from '@fg/storage';
import {
  type HudContainerHandle,
  type PauseOverlayHandle,
  type ScoreCounterHandle,
  createHudContainer,
  createPauseOverlay,
  createScoreCounter,
} from '@fg/ui-kit';
import Phaser from 'phaser';
import { FRUIT_TIERS, GAME_OVER, HUD, STORAGE_KEYS, THROW } from '../config';
import { Bowl } from '../entities/Bowl';
import { Fruit } from '../entities/Fruit';
import { Hand } from '../entities/Hand';
import { type AudioSystem, createAudioSystem } from '../systems/audioSystem';
import { type GameOverWatcher, createGameOverWatcher } from '../systems/gameOverSystem';
import { type MergeCandidate, type MergeEvent, handleCollisionPairs } from '../systems/mergeSystem';
import { createPlaceholderSfx } from '../systems/placeholderSfx';
import { pickNextTier } from '../systems/spawnSystem';

export class GameScene extends Phaser.Scene {
  private bowl!: Bowl;
  private hand!: Hand;
  private heldFruit!: Phaser.GameObjects.Sprite;
  private dangerLineGfx!: Phaser.GameObjects.Graphics;
  private fruits: Set<Fruit> = new Set();
  private fruitById: Map<number, Fruit> = new Map();
  private nextTier = 0;
  private score = 0;
  private rng!: Rng;
  private storage!: Storage;
  private audioSys!: AudioSystem;
  private hudContainer!: HudContainerHandle;
  private scoreCounter!: ScoreCounterHandle;
  private nextPreviewEl!: HTMLElement;
  private pauseBtnEl!: HTMLButtonElement;
  private pauseOverlay: PauseOverlayHandle | null = null;
  private goWatcher!: GameOverWatcher;
  private lifecycle!: ReturnType<typeof createLifecycle>;
  private debug = createDebugOverlay();
  private lastScoreMilestoneFired = 0;
  /** Device-pixel ratio — world coords are in physical px (Supernapie HiDPI). */
  private dpr = 1;

  constructor() {
    super('game');
  }

  create(): void {
    this.dpr = (this.registry.get('dpr') as number) ?? 1;

    // Constrain world so fruits cannot escape past the viewport.
    this.matter.world.setBounds(0, 0, this.scale.width, this.scale.height);

    this.storage = createStorage('fruit-merge-2d');
    // Snapshot/resume is disabled by design — every page load starts a fresh
    // game. Defensively clear any stale snapshot left over from earlier
    // versions of this code (or other tabs) so it never gets used.
    void this.storage.remove(STORAGE_KEYS.snapshot);
    this.rng = createRng();

    // Bowl geometry — explicit fractions of viewport (no sprite).
    // 540 is a CSS-px cap → scale to physical px.
    const interiorWidth = Math.min(this.scale.width * 0.78, 540 * this.dpr);
    const interiorTopY = this.scale.height * 0.3;
    const interiorBottomY = this.scale.height * 0.92;
    this.bowl = new Bowl({
      scene: this,
      x: this.scale.width / 2,
      interiorTopY,
      interiorBottomY,
      interiorWidth,
    });

    this.dangerLineGfx = this.add.graphics().setDepth(15);
    this.drawDangerLine(false);

    this.nextTier = pickNextTier(this.rng);
    const handWidth = 72 * this.dpr;
    this.hand = new Hand({
      scene: this,
      textureKey: 'hand',
      x: this.scale.width / 2,
      y: this.bowl.interiorTopY - 80 * this.dpr,
      width: handWidth,
      onRelease: (x, y) => this.spawnFruitAtThrow(x, y),
    });
    this.hand.sprite.setDepth(20);

    // Held-fruit preview sprite — sits below the hand, tracks hand x.
    this.heldFruit = this.add.sprite(
      this.hand.sprite.x,
      this.hand.sprite.y,
      `fruit-${FRUIT_TIERS[this.nextTier].name}`,
      0,
    );
    this.heldFruit.setDepth(19);
    this.applyHeldFruitTexture();

    this.hudContainer = createHudContainer();
    document.body.appendChild(this.hudContainer.el);
    this.scoreCounter = createScoreCounter({ initial: 0 });
    this.hudContainer.slot('top-center').appendChild(this.scoreCounter.el);

    this.pauseBtnEl = document.createElement('button');
    this.pauseBtnEl.type = 'button';
    this.pauseBtnEl.textContent = '⏸';
    this.pauseBtnEl.style.cssText =
      'background:rgba(255,255,255,0.9);border:none;border-bottom:3px solid rgba(0,0,0,0.12);width:36px;height:36px;border-radius:50%;font:800 14px system-ui;cursor:pointer;color:#f43f7c;';
    this.pauseBtnEl.onclick = () => this.openPause();
    this.hudContainer.slot('top-left').appendChild(this.pauseBtnEl);

    this.nextPreviewEl = document.createElement('div');
    this.nextPreviewEl.style.cssText =
      'background:rgba(255,255,255,0.92);border:none;border-bottom:3px solid rgba(0,0,0,0.12);padding:6px 12px;border-radius:14px;color:#2a1a1f;font:800 13px system-ui;display:flex;align-items:center;gap:8px;text-transform:uppercase;letter-spacing:1px;';
    this.hudContainer.slot('top-right').appendChild(this.nextPreviewEl);
    this.updateNextPreview();

    const audio = createAudio();
    const loadedAudio = (this.registry.get('loaded-audio') as Set<string>) ?? new Set();
    const baseUrl = import.meta.env.BASE_URL;
    void audio.load(
      [...loadedAudio].map((key) => ({
        key,
        src: [`${baseUrl}assets/audio/${key}.ogg`, `${baseUrl}assets/audio/${key}.mp3`],
      })),
    );
    const placeholder = createPlaceholderSfx();
    this.audioSys = createAudioSystem({ audio, placeholder, loaded: loadedAudio });

    const quality = detectQuality();
    this.debug.setMetric('quality', quality.tier);
    this.debug.setMetric('storage', this.storage.persistence);
    this.debug.setMetric('rng-seed', String(this.rng.seed()));
    this.debug.setMetric('dpr', `${this.dpr} (raw ${window.devicePixelRatio})`);

    this.goWatcher = createGameOverWatcher({
      fruits: () => [...this.fruits],
      dangerLineY: this.bowl.dangerLineY,
      graceMs: GAME_OVER.graceMs,
      onGameOver: () => {
        void this.handleGameOver();
      },
    });

    this.matter.world.on('collisionstart', this.onCollision, this);

    // Lifecycle handles document visibility / focus pause-resume only.
    // Snapshot/resume across reloads is intentionally disabled — onSnapshot
    // and onRestore are omitted so nothing is written to or read from storage.
    this.lifecycle = createLifecycle({ storage: this.storage });
    this.lifecycle.attach({
      onPause: () => {
        this.matter.world.pause();
        this.audioSys.setMuted(true);
        this.debug.log('lifecycle.pause');
      },
      onResume: () => {
        this.matter.world.resume();
        this.audioSys.setMuted(false);
        this.debug.log('lifecycle.resume');
      },
    });

    // Pointer wiring via Phaser's input system — `pointer.x/y` flow through
    // ScaleManager.transformX/Y so coords are in world (physical px) regardless
    // of the canvas/CSS size mismatch from HiDPI scaling. Raw DOM listeners
    // would return CSS px and be wrong by a factor of dpr.
    this.game.canvas.style.touchAction = 'none';
    this.input.on(Phaser.Input.Events.POINTER_MOVE, this.onPointerMove);
    this.input.on(Phaser.Input.Events.POINTER_DOWN, this.onPointerMove);
    this.input.on(Phaser.Input.Events.POINTER_UP, this.onPointerUp);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.shutdownScene());

    platform.loadingComplete();
    platform.gameplayStart();
  }

  private shutdownScene(): void {
    // Phaser auto-cleans scene-owned things on shutdown: Matter bodies + their
    // event listeners, sprites, input listeners. We only tear down resources
    // that live OUTSIDE the scene: DOM overlays, document/window listeners,
    // and the Web Audio context. Touching matter.world here is unsafe — it
    // may already be null by the time SHUTDOWN fires.
    this.lifecycle?.destroy();
    this.audioSys?.destroy();
    this.hudContainer?.destroy();
    this.scoreCounter?.destroy();
    this.debug?.destroy();
    this.pauseOverlay?.destroy();
    this.pauseOverlay = null;
    this.fruits.clear();
    this.fruitById.clear();
  }

  override update(_t: number, deltaMs: number): void {
    this.hand.tick(deltaMs);
    for (const f of this.fruits) f.syncSpriteToBody();
    // Held-fruit tracks hand x, anchored just below the hand
    this.heldFruit.x = this.hand.sprite.x;
    this.heldFruit.y = this.hand.sprite.y + this.hand.sprite.displayHeight * 0.18;
    this.goWatcher.tick(deltaMs);
    const above = this.goWatcher.isAboveLine();
    this.drawDangerLine(above);
    this.debug.setMetric('fps', String(Math.round(this.game.loop.actualFps)));
    this.debug.setMetric('fruits', String(this.fruits.size));
  }

  private applyHeldFruitTexture(): void {
    const tier = FRUIT_TIERS[this.nextTier];
    this.heldFruit.setTexture(`fruit-${tier.name}`, 0);
    const VISUAL_FRACTION = 0.78;
    const frame = this.heldFruit.frame;
    const shorter = Math.min(frame.width, frame.height);
    // tier.radius is CSS-px → scale to physical-px world coords for HiDPI.
    const desired = tier.radius * 2 * this.dpr;
    const scaleFactor = desired / (shorter * VISUAL_FRACTION);
    this.heldFruit.setDisplaySize(frame.width * scaleFactor, frame.height * scaleFactor);
  }

  private onPointerMove = (pointer: Phaser.Input.Pointer): void => {
    const pad = 20 * this.dpr;
    this.hand.setTargetX(
      Math.max(
        this.bowl.interiorLeftX + pad,
        Math.min(this.bowl.interiorRightX - pad, pointer.x),
      ),
    );
  };

  private onPointerUp = (): void => {
    this.audioSys.playThrow();
    this.hand.startThrow();
  };

  private spawnFruitAtThrow(x: number, y: number): void {
    // Throw velocities are tuned in CSS px/step — scale to physical px world.
    const vx = (Math.random() * 2 - 1) * THROW.jitterVx * this.dpr;
    this.spawnFruit(this.nextTier, x, y, vx, THROW.initialVy * this.dpr, 0);
    this.nextTier = pickNextTier(this.rng);
    this.updateNextPreview();
    this.applyHeldFruitTexture();
    this.debug.setMetric('next', FRUIT_TIERS[this.nextTier].name);
  }

  private spawnFruit(tier: number, x: number, y: number, vx = 0, vy = 0, angle = 0): Fruit {
    const fruit = new Fruit({
      scene: this,
      tier,
      x,
      y,
      vx,
      vy,
      angle,
      texturePrefix: 'fruit',
    });
    this.fruits.add(fruit);
    this.fruitById.set(fruit.body.id, fruit);
    return fruit;
  }

  private onCollision = (event: Phaser.Physics.Matter.Events.CollisionStartEvent): void => {
    const candidatePairs: Array<readonly [MergeCandidate, MergeCandidate]> = [];
    let anyFruitInvolved = false;
    for (const pair of event.pairs) {
      const a = this.fruitById.get(pair.bodyA.id);
      const b = this.fruitById.get(pair.bodyB.id);
      if (a && b) {
        candidatePairs.push([a, b]);
        anyFruitInvolved = true;
      } else if (a || b) {
        anyFruitInvolved = true;
      }
    }
    if (anyFruitInvolved) this.audioSys.playBounce();
    handleCollisionPairs(candidatePairs, (mergeEvent) => this.applyMerge(mergeEvent));
  };

  private applyMerge(event: MergeEvent): void {
    for (const id of event.removedIds) {
      const f = this.fruitById.get(id);
      if (f) {
        f.playHappy();
        f.destroy();
        this.fruits.delete(f);
        this.fruitById.delete(id);
      }
    }
    this.spawnFruit(event.newTier, event.x, event.y, event.vx, event.vy);
    const scoreDelta = FRUIT_TIERS[event.newTier]?.scoreOnMerge ?? 0;
    if (scoreDelta > 0) {
      this.score += scoreDelta;
      this.scoreCounter.bump(scoreDelta);
      const milestone = Math.floor(this.score / HUD.scorePulseMilestone);
      if (milestone > this.lastScoreMilestoneFired) {
        this.lastScoreMilestoneFired = milestone;
        this.scoreCounter.set(this.score);
      }
    }
    this.audioSys.playMerge(event.newTier);
    platform.happyTime(0.5);
    this.spawnMergePoof(event.x, event.y);
    this.debug.log(`merge tier=${event.newTier} score=${this.score}`);
  }

  private spawnMergePoof(x: number, y: number): void {
    const poof = this.add.image(x, y, 'vfx-merge-poof').setDepth(40);
    poof.setBlendMode(Phaser.BlendModes.ADD);
    poof.setScale(0.5);
    this.tweens.add({
      targets: poof,
      scale: 1.4,
      alpha: 0,
      duration: 350,
      ease: 'Cubic.easeOut',
      onComplete: () => poof.destroy(),
    });
  }

  private drawDangerLine(warning: boolean): void {
    this.dangerLineGfx.clear();
    const color = warning ? 0xff3b30 : 0xe51e50;
    const alpha = warning ? 0.8 : 0.4;
    this.dangerLineGfx.lineStyle(2 * this.dpr, color, alpha);
    const y = this.bowl.dangerLineY;
    const dashLen = 6 * this.dpr;
    const gap = 6 * this.dpr;
    for (let x = this.bowl.interiorLeftX; x < this.bowl.interiorRightX; x += dashLen + gap) {
      this.dangerLineGfx.beginPath();
      this.dangerLineGfx.moveTo(x, y);
      this.dangerLineGfx.lineTo(Math.min(x + dashLen, this.bowl.interiorRightX), y);
      this.dangerLineGfx.strokePath();
    }
  }

  private updateNextPreview(): void {
    const tier = FRUIT_TIERS[this.nextTier];
    this.nextPreviewEl.replaceChildren();
    const label = document.createElement('span');
    label.textContent = 'NEXT';
    // Strip is 1536×1024 (4 frames of 384×1024). The fruit drawing sits in the
    // vertical middle of each frame, so we render the strip at frame-aspect
    // and crop to a SQUARE viewport — same trick as the loading-screen logo.
    // Without this, the dot is ~117px tall on a 44px-wide element and dominates
    // the mobile HUD.
    const dot = document.createElement('div');
    const size = 30;
    const stripW = size * 4; // 4 frames laid out horizontally
    const stripH = Math.round((size * 1024) / 384); // preserve frame's vertical aspect
    const yOffset = Math.round((stripH - size) / 2); // crop to fruit center
    dot.style.cssText =
      `width:${size}px;height:${size}px;` +
      `background:url('${import.meta.env.BASE_URL}assets/sprites/fruit-${tier.name}.png') no-repeat;` +
      `background-size:${stripW}px ${stripH}px;background-position:0 -${yOffset}px;` +
      `filter:drop-shadow(0 2px 3px rgba(0,0,0,0.15));`;
    this.nextPreviewEl.appendChild(label);
    this.nextPreviewEl.appendChild(dot);
  }

  private openPause(): void {
    this.matter.world.pause();
    this.pauseOverlay = createPauseOverlay({
      onResume: () => {
        this.matter.world.resume();
        this.pauseOverlay?.destroy();
        this.pauseOverlay = null;
      },
      onRestart: () => {
        // Resume physics before the restart transition so Phaser tears down
        // the matter world from a clean state — otherwise SHUTDOWN may fire
        // with matter.world already null and our shutdown handler crashes.
        this.matter.world.resume();
        this.pauseOverlay?.destroy();
        this.pauseOverlay = null;
        this.scene.restart();
      },
      onQuit: () => {
        this.matter.world.resume();
        this.pauseOverlay?.destroy();
        this.pauseOverlay = null;
        this.scene.start('title');
      },
    });
    document.body.appendChild(this.pauseOverlay.el);
    this.pauseOverlay.show();
  }

  private async handleGameOver(): Promise<void> {
    this.matter.world.pause();
    this.audioSys.playGameOver();
    platform.gameplayStop();
    const prevBest = await this.storage.get<number>(STORAGE_KEYS.highScore, 0);
    if (this.score > prevBest) {
      await this.storage.set(STORAGE_KEYS.highScore, this.score);
      this.audioSys.playNewBest();
    }
    this.registry.set('game-over-score', this.score);
    this.registry.set('game-over-prev-best', prevBest);
    this.scene.launch('gameover');
  }
}
